import { useTranslation } from "react-i18next";
import type { InvitePreview } from "../../services/naraDoor";

interface Props {
  preview: InvitePreview;
  /** The signed-in account the invite would be accepted with. */
  account: string | null;
  onAccept: () => void;
  onSwitchAccount: () => void;
  onCancel: () => void;
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm font-medium break-words">{children}</dd>
  </div>
);

/** What accepting an invite would do, shown before anything is redeemed (T-191). */
export function InviteConfirm({ preview, account, onAccept, onSwitchAccount, onCancel }: Props) {
  const { t } = useTranslation();
  const role = t(`workspaceMembers.roles.${preview.resultingRole}`, { defaultValue: preview.resultingRole });
  return (
    <div className="w-full max-w-md space-y-4 text-left">
      <h1 className="text-lg font-semibold text-center">{t("invite.confirm.title")}</h1>
      <p className="text-sm text-muted-foreground text-center">{t("invite.confirm.intro")}</p>
      <dl className="space-y-3 rounded border p-4">
        {account && <Row label={t("invite.confirm.account")}>{account}</Row>}
        <Row label={t("invite.confirm.workspace")}>{preview.workspaceName}</Row>
        <Row label={t("invite.confirm.role")}>{role}</Row>
        {preview.projectNames.length > 0 && (
          <Row label={t("invite.confirm.projects")}>
            <ul className="list-disc pl-4">
              {preview.projectNames.map((name, i) => (
                <li key={`${i}-${name}`}>{name}</li>
              ))}
            </ul>
          </Row>
        )}
      </dl>
      <div className="flex flex-col gap-2">
        <button
          onClick={onAccept}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {t("invite.confirm.accept")}
        </button>
        <button onClick={onSwitchAccount} className="px-4 py-2 text-sm font-medium border rounded hover:bg-muted">
          {t("invite.confirm.switchAccount")}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-muted-foreground hover:underline">
          {t("invite.confirm.cancel")}
        </button>
      </div>
    </div>
  );
}
